import { useSyncExternalStore } from 'react';

import {
    type ChipHandle,
    type ChipKind,
    getChatChipRegistryEntry,
} from '@apps/web/features/admin_agent_dock/chip_registry';

// Tiny external store that lets any /admin surface ask the dock to
// attach chips to the current chat ("+ add to chat", the chip-source
// counterpart of `route_chip_source.ts`). Surfaces enqueue requests;
// the always-mounted dock consumes them, because the dock alone owns
// the active-chat selection, the create-vs-update decision, and the
// chip-set mutation path. Requests resolve with the outcome so the
// originating affordance can confirm inline (added / already there /
// over the limit) without duplicating any chat state.

// Hard ceiling on chips per chat. Bulk adds clamp to the remaining
// capacity and report the overflow instead of failing.
export const MAX_CHIPS_PER_CHAT = 10;

export type ChipAddResult = {
    // Chips newly appended to the chat's chip set.
    readonly added: number;
    // Chips skipped because the same (kind, entity_id) was already
    // attached — dedupe is a silent no-op per the chip-source spec.
    readonly duplicate: number;
    // Chips skipped because the chat hit `MAX_CHIPS_PER_CHAT`.
    readonly overflowed: number;
};

export type ChipAddRequest = {
    readonly chips: ReadonlyArray<ChipHandle>;
    // Chat the request targets, bound at request start (button click
    // time). `null` means "no chat was active when the user clicked";
    // only in that case may the dock fall back to creating a new
    // chat. Binding here — instead of reading the dock's active-tab
    // ref when the serialized work item eventually runs — means a
    // user who clicks on chat A and then switches the dock to chat B
    // while the request is queued still lands the chip on chat A
    // (round-7 P1).
    readonly chatId: string | null;
    // Optional abort signal owned by the calling surface. The dock
    // re-checks `signal?.aborted` right before running the mutation
    // so a caller that unmounted BETWEEN the queue drain and the
    // serialized write executing never lands a chip on the wrong
    // chat (round-4 P2-1). The abort listener in
    // `requestAddChipsToChat` also strips the request from the
    // pending array while it is still queued — the two guards
    // cover the pre-drain and post-drain windows respectively.
    readonly signal: AbortSignal | null;
    readonly resolve: (result: ChipAddResult) => void;
    readonly reject: (error: unknown) => void;
};

let pending: ReadonlyArray<ChipAddRequest> = [];
const listeners = new Set<() => void>();

function emit(): void {
    for (const listener of listeners) {
        listener();
    }
}

// An entity kind is add-to-chat-capable iff its chip kind has a
// registry entry — registration in `chip_registry.ts` is the single
// switch that lights up every affordance for that kind.
export function isAddToChatCapable(kind: ChipKind): boolean {
    return getChatChipRegistryEntry(kind) !== null;
}

export function requestAddChipsToChat(
    chips: ReadonlyArray<ChipHandle>,
    options?: {
        readonly signal?: AbortSignal;
        // Bound at click time by the calling surface (via
        // `readCurrentAdminAgentChatId`). Omitting or passing `null`
        // signals "no active chat"; the dock will create one.
        readonly chatId?: string | null;
    },
): Promise<ChipAddResult> {
    return new Promise((resolve, reject) => {
        const signal = options?.signal ?? null;
        const chatId = options?.chatId ?? null;
        const request: ChipAddRequest = {
            chips,
            chatId,
            signal,
            resolve,
            reject,
        };
        if (signal?.aborted) {
            // Never enqueue an already-aborted request. The caller has
            // already unmounted / navigated away; a queued handler
            // would consume this into whichever chat happens to be
            // active when the dock next drains.
            reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
            return;
        }
        pending = [...pending, request];
        emit();
        if (signal) {
            const onAbort = () => {
                // Drop the request from the queue so the dock never
                // consumes it after the caller went away (round-3
                // P2-1). If the dock already drained it, the signal
                // is still attached to the request — the dock rechecks
                // `signal.aborted` right before mutating and skips the
                // write (round-4 P2-1). Either way, the caller's
                // promise rejects.
                if (pending.includes(request)) {
                    pending = pending.filter((r) => r !== request);
                    emit();
                }
                reject(
                    signal.reason ?? new DOMException('Aborted', 'AbortError'),
                );
            };
            signal.addEventListener('abort', onAbort, { once: true });
        }
    });
}

export function getPendingChipAddRequests(): ReadonlyArray<ChipAddRequest> {
    return pending;
}

// Drain the queue. The dock takes ownership of every pending request
// and must settle each one's promise.
export function takePendingChipAddRequests(): ReadonlyArray<ChipAddRequest> {
    const taken = pending;
    if (taken.length > 0) {
        pending = [];
        emit();
    }
    return taken;
}

export function subscribeToChipAddRequests(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

export function usePendingChipAddRequests(): ReadonlyArray<ChipAddRequest> {
    return useSyncExternalStore(
        subscribeToChipAddRequests,
        getPendingChipAddRequests,
    );
}
